import { useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { resolveTenantPath } from "./tenantUrl";

type SearchParamsInput = Record<
  string,
  string | number | boolean | null | undefined
>;

export default function useNavigateFrom() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams(); // note: setSearchParams is not needed here
    const tenantId = useSelector((state: any) => state.auth.user?.tenant?.tenant_id);

    const from = searchParams.get("from");

    const getNextRoute = (route: string, persistent_from = false, params: Record<string, any> = {}) => {
        route = resolveTenantPath(route, tenantId);

        // A route may already carry its own query string (e.g. a cell-filtered
        // "/signals?facility_location_id=…"). Peel it off and merge it into the
        // params instead of appending a second "?", which would otherwise fold
        // the whole inherited query into that route param's value.
        const [routePath, routeQuery = ""] = route.split("?");
        const nextParams = new URLSearchParams(searchParams);
        new URLSearchParams(routeQuery).forEach((value, key) => nextParams.set(key, value));

        // Add or update incoming params
        Object.entries(params).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                nextParams.delete(key);
            } else {
                nextParams.set(key, String(value));
            }
        });

        // Set or update the `from` param
        if (persistent_from && from) {
            nextParams.set("from", from);
        } else {
            nextParams.set("from", location.pathname + location.search);
        }

        return `${routePath}?${nextParams.toString()}`;
    };

    const navigateTo = useCallback((
        route: string,
        persistent_from = false,
        params: Record<string, any> = {},
        replace = false
    ) => {
        const nextRoute = getNextRoute(route, persistent_from, params);
        navigate(nextRoute, { replace });
    }, [navigate, from, location.pathname, location.search, searchParams, tenantId]);

    const getPrevRoute = useCallback((fallback = "/") => from || fallback, [from]);
    const navigateBack = useCallback((fallback = "/") => navigate(from || fallback), [navigate, from]);

    // TODO the goal is to remove the logic above
    const goBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/");
        }
    };

    const updateSearchParams = useCallback(
        (newParams: SearchParamsInput) => {
            const params = new URLSearchParams(searchParams);

            Object.entries(newParams).forEach(([key, value]) => {
                if (value === null || value === undefined) {
                    params.delete(key);
                } else {
                    params.set(key, value.toString());
                }
            });

            navigate(
                {
                    pathname: location.pathname,
                    search: params.toString(),
                },
                { replace: true }
            );
        },
        [navigate, location.pathname, searchParams]
    );

    return { getNextRoute, getPrevRoute, navigateTo, navigateBack, goBack, updateSearchParams };
}

export function FromLink({ to, children, ...props }) {
    const { getNextRoute } = useNavigateFrom();

    return <Link to={getNextRoute(to)} {...props}>{children}</Link>;
}
